import { yupResolver } from '@hookform/resolvers/yup'
import SaveIcon from '@mui/icons-material/Save'
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Select from '@mui/material/Select'
import useSWR from 'swr'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { countries } from '~/src/constants'
import { otherRepository } from '~/src/repositories'
import { TUserProfile } from '~/src/repositories/User/models/User'
import { profileSchema } from '~/src/schemas'
import { TProfileFormValues } from '~/src/types/form'

interface IProfileFormProps {
  data: TUserProfile
  onSave: (formValue: TProfileFormValues) => void
  isLoadingSave: boolean
}

export const ProfileForm: React.FC<IProfileFormProps> = ({ data, onSave, isLoadingSave }) => {
  const { t } = useTranslation()
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<TProfileFormValues>({
    defaultValues: data,
    resolver: yupResolver(profileSchema),
    reValidateMode: 'onBlur',
  })

  const { data: loadingPorts } = useSWR(['/loading-ports'], () =>
    otherRepository.getLoadingPorts(),
    {
      fallbackData: [],
    }
  )

  const isSigned = watch('isSigned')

  useEffect(() => {
    reset(data)
  }, [data, reset])

  return (
    <Paper sx={{ mb: '20px' }}>
      <Typography
        variant={'h2'}
        sx={{
          fontFamily: 'Roboto',
          padding: 2,
          fontSize: 20,
          fontStyle: 'normal',
          lineHeight: '32px',
          fontWeight: 'medium',
        }}
      >
        {t('user_detail.profile')}
      </Typography>
      <Box
        sx={{
          borderTop: '1px solid rgba(224, 224, 224, 1)',
          padding: 2,
          borderBottom: '1px solid rgba(224, 224, 224, 1)',
        }}
      >
        <Grid container spacing={2}>
          <Grid container item spacing={2}>
            <Grid item lg={3}>
              <TextField
                fullWidth
                required
                variant="outlined"
                size="small"
                id="outlined-adornment-company-name"
                label={t('user_detail.company_name')}
                {...register('companyName')}
                error={errors.companyName ? true : false}
                helperText={errors.companyName?.message}
              />
            </Grid>
            <Grid item lg={3}>
              <TextField
                fullWidth
                required
                variant="outlined"
                size="small"
                id="outlined-adornment-name"
                label={t('user_detail.name')}
                {...register('name')}
                error={errors.name ? true : false}
                helperText={errors.name?.message}
              />
            </Grid>
            <Grid item lg={6}>
              <TextField
                fullWidth
                required
                variant="outlined"
                size="small"
                id="outlined-adornment-email"
                label={t('user_detail.email')}
                value={data.email}
                disabled
              />
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item lg={3}>
              <TextField
                fullWidth
                required
                variant="outlined"
                size="small"
                id="outlined-adornment-phone"
                label={t('user_detail.phone')}
                {...register('tel')}
                error={errors.tel ? true : false}
                helperText={errors.tel?.message}
              />
            </Grid>
            <Grid item lg={3}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                id="outlined-adornment-fax"
                label={t('user_detail.fax')}
                {...register('fax')}
                error={errors.fax ? true : false}
                helperText={errors.fax?.message}
              />
            </Grid>
            <Grid item lg={3}>
              <TextField
                fullWidth
                required
                variant="outlined"
                size="small"
                id="outlined-adornment-mobile"
                label={t('user_detail.mobile')}
                {...register('mobile')}
                error={errors.mobile ? true : false}
                helperText={errors.mobile?.message}
              />
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item lg={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel htmlFor="outlined-adornment-country">
                  {t('user_detail.country')}
                </InputLabel>
                <Controller
                  name="countryId"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      required
                      MenuProps={{
                        style: {
                          marginTop: 2,
                          maxHeight: '410px',
                          maxWidth: '200px',
                        },
                      }}
                      labelId="outlined-adornment-country"
                      id="outlined-adornment-country"
                      label={t('user_detail.country')}
                      value={value}
                      onChange={onChange}
                    >
                      {countries.map((country, index) => (
                        <MenuItem key={index} value={`${country.id}`}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item lg={3}>
              <TextField
                fullWidth
                required
                variant="outlined"
                size="small"
                id="outlined-adornment-city"
                label={t('user_detail.city')}
                {...register('city')}
                error={errors.city ? true : false}
                helperText={errors.city?.message}
              />
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item lg={12}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                id="outlined-adornment-address1"
                label={t('user_detail.address_1')}
                {...register('address1')}
                error={errors.address1 ? true : false}
                helperText={errors.address1?.message}
              />
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item lg={12}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                id="outlined-adornment-address2"
                label={t('user_detail.address_2')}
                {...register('address2')}
                error={errors.address2 ? true : false}
                helperText={errors.address2?.message}
              />
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item lg={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel htmlFor="outlined-adornment-loading-port">
                  {t('user_detail.loading_port')}
                </InputLabel>
                <Controller
                  name="loadingPortId"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select
                      MenuProps={{
                        style: {
                          marginTop: 2,
                          maxHeight: '410px',
                          maxWidth: '200px',
                        },
                      }}
                      labelId="outlined-adornment-loading-port"
                      id="outlined-adornment-loading-port"
                      label={t('user_detail.loading_port')}
                      value={value}
                      onChange={onChange}
                    >
                      <MenuItem value={""}></MenuItem>
                      {loadingPorts.map((port, index) => (
                        <MenuItem key={index} value={port.id}>
                          {port.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item lg={12}>
              <Typography>{t('user_detail.basic_contract')}</Typography>
              <Box pl={'96px'}>
                <Controller
                  name="isSigned"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <RadioGroup
                        aria-labelledby="radio-buttons-group-label"
                        value={value}
                        name="isSigned"
                        onChange={onChange}
                      >
                        <FormControlLabel
                          value={0}
                          control={<Radio />}
                          label={`${t('user_detail.not_concluded')}`}
                        />
                        <FormControlLabel
                          value={1}
                          control={<Radio />}
                          label={`${t('user_detail.concluded')}`}
                        />
                      </RadioGroup>
                      {error && <FormHelperText>{error.message}</FormHelperText>}
                    </>
                  )}
                />
              </Box>
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item lg={12}>
              <Typography>{t('user_detail.kra_cooperation')}</Typography>
              <Grid container item lg={12} spacing={2}>
                <Grid item lg={6}>
                  <Stack direction={'column'} ml={'103px'} mt={'23px'}>
                    <Stack direction={'row'} spacing={3}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        label={t('user_detail.token')}
                        id="outlined-adornment-amount"
                        {...register('token')}
                        error={errors.token ? true : false}
                        helperText={errors.token?.message}
                        />
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item lg={12}>
              <Typography sx={{ opacity: Number(isSigned) === 0 ? 0.5 : 1 }}>
                {t('user_detail.genuine_part_search')}
              </Typography>
              <Box pl={'96px'}>
                <Controller
                  name="canSearchGenuinePart"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <RadioGroup
                        aria-labelledby="radio-buttons-group-label"
                        value={value}
                        name="canSearchGenuinePart"
                        onChange={onChange}
                      >
                        <FormControlLabel
                          value={1}
                          control={<Radio disabled={Number(isSigned) === 0} />}
                          label={`${t('user_detail.available')}`}
                        />
                        <FormControlLabel
                          value={0}
                          control={<Radio disabled={Number(isSigned) === 0} />}
                          label={`${t('user_detail.unavailable')}`}
                        />
                      </RadioGroup>
                      {error && <FormHelperText>{error.message}</FormHelperText>}
                    </>
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      <Box display={'flex'} justifyContent={'flex-end'} sx={{ padding: 2 }}>
        <Button
          onClick={handleSubmit(onSave)}
          variant="contained"
          disabled={!isDirty || isLoadingSave}
        >
          <SaveIcon fontSize={'small'} />
          <Typography
            sx={{
              fontSize: 15,
              fontStyle: 'normal',
              fontWeight: 'medium',
              lineHeight: '26px',
              color: 'white',
              ml: '11px',
            }}
          >
            {t('user_detail.save')}
          </Typography>
        </Button>
      </Box>
    </Paper>
  )
}
